import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  FileText,
  MousePointerClick,
  PencilLine,
  Plus,
  RotateCcw,
  Ruler,
  Layers,
  Spline,
  SquareMinus,
  SquarePlus,
  Trash2,
  Wrench,
  type LucideIcon,
} from "lucide-react";
import { BoardNameEntry } from "../BoardNameEntry";
import { BoardFeedsEntry } from "../BoardFeedsEntry";
import { HeightEntry } from "../HeightEntry";
import { PanelSelect } from "../../PanelSelect";
import type { HeightTarget } from "../installationHeights";
import { DiagramPageModal } from "../DiagramPageModal";
import { useKarlBeatClass } from "../karlMotion";
import {
  applyAskPlanMove,
  askPlanMove,
  isToolActive,
  resolveAnswerTones,
  splitAnswerPair,
} from "./askCardModel";
import type { KarlAnswerTone } from "./askCardModel";
import type { KarlAskArm } from "../karlAskQueue";
import type { KarlAnswer, KarlAsk, KarlAskTool } from "../karlAsks";
import type { KarlAskCardProps } from "./types";

type ArmedLine = (arm: KarlAskArm) => { key: string; params?: Record<string, string> };

// One size for all three rungs. They were 13.5 / 13 / 12.5 — half-pixel steps
// nobody can resolve, least of all at 55 with reading glasses, so the ladder
// read as uniformly small and nothing was salient. Height and spacing are set
// for a mouse arriving from the floor plan 700px away, 86 times a sitting.
const TONE_CLASS: Record<KarlAnswerTone, string> = {
  primary:
    "w-full rounded-md bg-blue-600 px-3 py-3 text-left text-[16px] font-semibold leading-snug text-white transition-opacity hover:opacity-90",
  default:
    "karl-hover-dwell w-full rounded-md border border-slate-300 bg-white px-3 py-3 text-left text-[16px] font-medium leading-snug text-slate-800 hover:bg-slate-100",
  quiet:
    "karl-hover-dwell w-full rounded-md px-3 py-2.5 text-left text-[16px] text-slate-700 hover:bg-slate-100",
};

const NAV_CLASS =
  "karl-hover-dwell flex items-center gap-1 rounded-md px-3 py-2 text-[14px] text-slate-600 hover:bg-slate-100 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-slate-600";

// The pair shipped icon-only and the owner could not read it: "whats doing x
// exactly on card, i cannot understand it tbh". An X glyph says reject/cancel/
// close; the answer under it means "no, some of these devices don't belong on
// this run", which arms a device pick. No glyph carries that, and a tooltip
// needs a hover he will not perform 82 times. So the words are back.
//
// A yes/no is still not a list: the two answers sit side by side on ONE line,
// where three answers stack. Both rungs are 48px tall — a mis-click here
// settles a run — and 12px apart, the widest gutter on the card.
const ANSWER_PAIR_CLASS =
  "flex min-h-[48px] min-w-0 flex-1 basis-0 items-center justify-center gap-1.5 rounded-md px-3 py-2 text-center text-[16px] leading-snug transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

// The tick survives on approve alone, and it is `splitAnswerPair` that earns it:
// the leading answer is guaranteed `accept`, so "it is right as it stands" is
// the only thing that button can ever mean. Nothing constrains what REJECT
// means — "Move it", "No, some don't belong", "Take them out" — so it gets no
// glyph, because one glyph cannot be honest about all three.
const APPROVE_CLASS = "bg-blue-600 font-semibold text-white hover:opacity-90";

// Outlined where approve is filled, exactly as the ladder's second rung is, so
// no colour has to be invented for it — and none of green/amber/indigo/violet
// gets a second meaning.
const REJECT_CLASS =
  "karl-hover-dwell border border-slate-300 bg-white font-medium text-slate-800 hover:bg-slate-100";

const WAY_OUT_CLASS =
  "karl-hover-dwell w-full rounded-md px-2 py-2 text-center text-[14px] text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50";

// Something he DOES to the run, not something he says about it. Tiles, not a
// row of identical grey wrenches: three tools that read as one string is three
// tools he never reaches for. Still quiet — reaching for one must never read as
// settling the question.
// One line, icon over label, each tile an equal share of the row: three
// identical grey wrenches read as one string of text at question 60, and
// nothing in it invited a click.
// Five across a 420px panel leaves each tile about 70px, so the label is given
// every pixel of it and ellipsises rather than crossing into its neighbour —
// the whole sentence is on the tile as its `title` either way.
const TOOL_CLASS =
  "karl-hover-dwell flex min-w-0 flex-1 basis-0 flex-col items-center justify-center gap-1 rounded-lg border px-0.5 py-2 text-center text-[13px] font-medium leading-tight disabled:cursor-not-allowed disabled:opacity-50";

const TOOL_IDLE_CLASS =
  "border-slate-300 bg-white text-slate-700 hover:border-blue-400 hover:bg-blue-50 hover:text-blue-700";

// The mode he is standing in. Filled and ringed, not merely hovered: at
// question 60 he has to be able to see which tool is live without moving the
// mouse to find out.
const TOOL_ACTIVE_CLASS =
  "border-blue-600 bg-blue-100 text-blue-800 ring-1 ring-blue-600";

// The same tile, and a different colour on purpose: the four are verbs, this
// one only shows him the sheet. Indigo is already the schematic's colour on
// this surface.
const REFERENCE_CLASS =
  "border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100";

const TOOL_ICON: Record<string, LucideIcon> = {
  addDevices: SquarePlus,
  removeDevices: SquareMinus,
  redrawRoute: Spline,
  rename: PencilLine,
  notABoard: Trash2,
  addBoard: Plus,
  editHeight: Ruler,
  offFloor: Layers,
};

const armedLine: ArmedLine = (arm) => {
  if (arm.kind === "moveSource")
    return { key: "smartSurveyor.cablesBeta.askArmedMove", params: { board: arm.label } };
  if (arm.kind === "placeSource")
    return { key: "smartSurveyor.cablesBeta.askArmedPlace", params: { board: arm.name } };
  if (arm.kind === "pickRoute")
    return { key: "smartSurveyor.cablesBeta.askArmedPickRoute" };
  if (arm.kind === "pickUpstreamFor")
    return { key: "smartSurveyor.cablesBeta.askArmedUpstream", params: { board: arm.toLabel } };
  if (arm.kind === "drawZone")
    return { key: "smartSurveyor.cablesBeta.askArmedZone", params: { board: arm.label } };
  return { key: "smartSurveyor.cablesBeta.askArmedBox" };
};

export const KarlAskCard = ({
  view,
  onAnswer,
  onRunTool,
  onBack,
  onForward,
  onWalkAgain,
  onCancelArm,
  isPicking = false,
  pickedCount = 0,
  onConfirmPick,
  plan,
  diagram,
  activeTool = null,
  shapeControls,
  isActive = true,
  isBusy = false,
  boardControls,
}: KarlAskCardProps) => {
  const { t } = useTranslation();
  const movedFor = useRef<string | null>(null);
  const [isDiagramPageOpen, setIsDiagramPageOpen] = useState(false);
  const [nameEntry, setNameEntry] = useState<
    { kind: "rename"; label: string } | { kind: "add" } | null
  >(null);
  const [feedsEntryFor, setFeedsEntryFor] = useState<string | null>(null);
  const [heightEntry, setHeightEntry] = useState<{ target: HeightTarget; heightM: number } | null>(
    null,
  );
  const [typeEntry, setTypeEntry] = useState<{ toLabel: string; cableType: string; options: string[] } | null>(
    null,
  );

  const ask = view.ask;
  const isArmed = view.mode === "armed" && Boolean(view.arm);
  // A TOOL leaves the question open by design, so the ladder stays up and he
  // can finish the run and answer it. Only an ANSWER's arm takes the card over:
  // there he is out on the drawing settling the question itself.
  const isAnswerArm = isArmed && !view.isToolArm;
  const tones = useMemo(() => resolveAnswerTones(ask?.answers ?? []), [ask]);
  // A yes/no becomes the tick and the cross. Anything else — three substantive
  // answers, or a leading answer that is not "accept it as it stands" — keeps
  // its worded ladder: two icons cannot carry three answers, and an answer that
  // fits nowhere is an answer that was deleted.
  const pair = useMemo(() => splitAnswerPair(ask?.answers ?? []), [ask]);
  const move = useMemo(() => askPlanMove(ask), [ask]);
  const diagramRefs = ask?.diagramRefs;
  // Keyed by the ask, so typing anywhere else in the panel does not re-scan the
  // schematic. The designator that found the page is kept with it, and its
  // picture is taken in the same pass: opening the full view — or the page
  // itself — on a different one of the ask's names lands on a different page.
  const diagramHit = useMemo(() => {
    if (!diagram || !diagramRefs?.length) return null;
    for (const designator of diagramRefs) {
      const page = diagram.pageOf(designator);
      if (typeof page === "number")
        return {
          designator,
          page,
          imageUrl: diagram.pageImageOf(designator),
          documentKey: diagram.diagramKeyOf(designator),
        };
    }
    return null;
  }, [diagram, diagramRefs]);
  const beatClass = useKarlBeatClass(view.position);
  const quietFrom = tones.indexOf("quiet");
  const wayOut = pair?.wayOut ?? null;
  const armed = isAnswerArm && view.arm ? armedLine(view.arm) : null;
  const toolArm = isArmed && !isAnswerArm;
  /**
   * One tool on at a time, and clicking the one that is on turns it off.
   *
   * The switch is enforced here as well as in the edit layer because the QUEUE
   * has to be told too: it holds the arm behind Add and Remove, and an arm left
   * standing would keep its waiting strip up under a tile that is no longer
   * lit. Reaching for another tool while devices are cut off puts the question
   * about them rather than switching — leaving the mode costs that question
   * however he leaves it, and silently dropping it would strand the devices
   * with no cable and nobody ever asked.
   */
  const runTool = (tool: KarlAskTool) => {
    // Both take a name, and the card is where he is typing — the queue never hears of them.
    if (tool.action.kind === "renameSource" && boardControls) {
      setNameEntry({ kind: "rename", label: tool.action.label });
      return;
    }
    if (tool.action.kind === "addSource" && boardControls) {
      setNameEntry({ kind: "add" });
      return;
    }
    // "Another floor" settles the question, so the card answers with it rather than running it as a tool.
    if (tool.action.kind === "setFeederOffFloor" && ask) {
      onAnswer(ask, { id: "offFloor", label: t(`smartSurveyor.cablesBeta.${tool.labelKey}`), action: tool.action });
      return;
    }
    // The checklist is filled in on the card; the question stays and "Ja" then confirms the new set.
    if (tool.action.kind === "editBoardFeeds" && boardControls?.feeds) {
      setFeedsEntryFor(tool.action.label);
      return;
    }
    const isOn = isToolActive({ tool, activeTool });
    if (isOn) {
      if (activeTool?.kind === "shape") shapeControls?.end();
      else onCancelArm();
      return;
    }
    if (activeTool?.kind === "shape" && shapeControls) {
      shapeControls.end();
      if (shapeControls.cutCount > 0) return;
    }
    if (activeTool && activeTool.kind !== "shape") onCancelArm();
    onRunTool?.(ask as KarlAsk, tool);
  };

  // "Höhe ändern" opens the entry here; what he types there is the answer the queue hears.
  const answerAsk = (answer: KarlAnswer) => {
    if (!ask) return;
    if (answer.action.kind === "editHeight") {
      setHeightEntry({ target: answer.action.target, heightM: answer.action.heightM });
      return;
    }
    if (answer.action.kind === "editFeederType") {
      setTypeEntry({
        toLabel: answer.action.toLabel,
        cableType: answer.action.cableType,
        options: answer.action.options,
      });
      return;
    }
    onAnswer(ask, answer);
  };

  // This run's shape, not any run's: an open shape elsewhere is not this card's
  // business, exactly as the tool tile's own active state decides it.
  const isReshapingThisRun = Boolean(
    activeTool?.kind === "shape" &&
      ask?.tools?.some(
        (tool) =>
          tool.action.kind === "editRouteShape" &&
          tool.action.circuitId === activeTool.circuitId,
      ),
  );
  // Hidden while armed for the same reason the ladder is: he is out on the
  // drawing doing one thing, and a second thing to click is how he loses it.
  const tools = !isAnswerArm && ask?.tools?.length ? ask.tools : null;

  // Guarded by the ask it last ran for, so a re-render never yanks the viewport
  // back while he is panning around the answer.
  useEffect(() => {
    if (!isActive || !plan) return;
    const stamp = ask?.id ?? "";
    if (movedFor.current === stamp) return;
    movedFor.current = stamp;
    applyAskPlanMove({ plan, move });
  }, [isActive, plan, ask, move]);

  useEffect(() => {
    setNameEntry(null);
    setFeedsEntryFor(null);
    setHeightEntry(null);
    setTypeEntry(null);
  }, [ask?.id]);

  // Clearing the marks also forgets which ask they were for: React mounts every card twice in
  // development, and the first ask's tray was lit, cleared here, and never lit again.
  useEffect(
    () => () => {
      movedFor.current = null;
      plan?.highlight(null);
      plan?.focusCircuit(null);
      plan?.highlightTrayType?.(null);
    },
    [plan],
  );

  // Also the shape a revisited ask takes when the rebuild no longer carries it.
  if (!ask) {
    return (
      <div className="karl-rise rounded-xl border border-emerald-200 bg-emerald-50/60 px-3 py-3">
        <div className="flex items-center gap-1.5 text-[13.5px] text-slate-700">
          <Check size={14} className="flex-none text-emerald-600" />
          {view.total
            ? t("smartSurveyor.cablesBeta.askAllDone", { count: view.total })
            : t("smartSurveyor.cablesBeta.askNone")}
        </div>
        {(view.canGoBack || view.canGoForward) && (
          <div className="mt-2 flex items-center gap-2">
            {view.canGoBack && (
              <button type="button" onClick={onBack} className={NAV_CLASS}>
                <ChevronLeft size={13} />
                {t("smartSurveyor.cablesBeta.askBack")}
              </button>
            )}
            {view.canGoBack && onWalkAgain && (
              <button type="button" onClick={onWalkAgain} className={NAV_CLASS}>
                <RotateCcw size={12} />
                {t("smartSurveyor.cablesBeta.askWalkAgain")}
              </button>
            )}
            {view.canGoForward && (
              <button type="button" onClick={onForward} className={clsx(NAV_CLASS, "ml-auto")}>
                {t("smartSurveyor.cablesBeta.askForward")}
                <ChevronRight size={13} />
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="karl-rise">
      {/* Keyed by the ask so the waiting ring pulses its three times per
          question, not once for the whole walkthrough. */}
      <div
        key={ask.id}
        data-karl-ask={ask.id}
        data-testid={`karl-ask-${ask.group}`}
        data-karl-mode={view.mode}
        className={clsx(
          "rounded-xl border border-blue-200 bg-blue-50/60 px-3 py-3",
          // While armed the ring belongs to the line he is waiting on, not the card.
          !isAnswerArm && "karl-await",
        )}
      >
        {/* The upper block carries the whole question — its key, his place in
            the walk, the sentence itself. The answer follows it directly. */}
        <div className="min-w-0">
          {/* The run's own key, in the slot a tracked-out uppercase group
              label used to hold — it said "CABLE RUNS" identically on all 86
              questions, in the hardest style on the card to resolve, above a
              question that already began "Now the runs". The key is what he
              is looking for. */}
          <div className="flex items-baseline gap-2">
            <span
              data-testid="karl-ask-subject"
              className="min-w-0 truncate text-[21px] font-semibold leading-none tabular-nums text-slate-900"
            >
              {ask.subject || t(`smartSurveyor.cablesBeta.askGroup.${ask.group}`)}
            </span>
            <span className="ml-auto flex-none text-[14px] tabular-nums text-slate-600">
              {/* Boards, not questions: each board's follow-ups were moving "Frage 6 von 59" while he answered. */}
              {ask.group === "sources"
                ? view.subjectPosition > 0 &&
                  t("smartSurveyor.cablesBeta.askBoardPosition", {
                    index: view.subjectPosition,
                    total: view.subjectTotal,
                  })
                : t("smartSurveyor.cablesBeta.askPosition", {
                    index: view.position,
                    total: view.total,
                  })}
            </span>
          </div>

          {/* What he actually asks when he asks "can I stop": if I close
              this, do I lose the answers? It is already true — saying it is
              the whole fix. Two words beside it carry what a sentence of grey
              prose did. */}
          <div className="mt-1 flex flex-wrap items-center gap-2 text-[14px] text-slate-600">
            <span
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={view.total}
              aria-valuenow={view.behind}
              aria-label={t("smartSurveyor.cablesBeta.askProgressLabel")}
              data-testid="karl-progress-fill"
            >
              {t("smartSurveyor.cablesBeta.askAnsweredSaved", {
                count: view.behind,
              })}
            </span>
            {view.isDeferred && (
              <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[13px] font-medium text-amber-800">
                {t("smartSurveyor.cablesBeta.askDeferredChip")}
              </span>
            )}
            {view.mode === "revisiting" && (
              <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[13px] font-medium text-slate-700">
                {t("smartSurveyor.cablesBeta.askChangeAnswerChip")}
              </span>
            )}
          </div>

          <div className={clsx("mt-3", beatClass)}>
            <div className="text-[17px] font-semibold leading-[1.45] text-slate-800">
              {ask.question}
            </div>
            {ask.detail && (
              <div className="mt-1 text-[14px] leading-[1.5] text-slate-600">
                {ask.detail}
              </div>
            )}
          </div>
        </div>

        {/* Yes and no, worded, side by side under the question. Only the tick
            is drawn, and only because `splitAnswerPair` guarantees the answer
            it sits on is `accept`. */}
        {pair && !isAnswerArm && (
          <div
            data-testid="karl-answer-ladder"
            data-karl-answers="pair"
            className={clsx(
              "mt-3",
              isBusy && "pointer-events-none select-none opacity-50",
            )}
          >
            <div data-testid="karl-answer-pair" className="flex items-stretch gap-3">
              <button
                type="button"
                disabled={isBusy}
                data-karl-answer={pair.approve.id}
                onClick={() => answerAsk(pair.approve)}
                className={clsx(ANSWER_PAIR_CLASS, APPROVE_CLASS)}
              >
                <Check size={18} strokeWidth={2.75} className="flex-none" />
                <span className="min-w-0">{pair.approve.label}</span>
              </button>
              <button
                type="button"
                disabled={isBusy}
                data-karl-answer={pair.reject.id}
                onClick={() => answerAsk(pair.reject)}
                className={clsx(ANSWER_PAIR_CLASS, REJECT_CLASS)}
              >
                <span className="min-w-0">{pair.reject.label}</span>
              </button>
            </div>
            {wayOut && (
              // Below a rule, as it always was: the thing telling him this is
              // not an answer has to be visible.
              <div
                data-testid="karl-answer-way-out"
                className="mt-2.5 border-t border-slate-300 pt-2"
              >
                <button
                  type="button"
                  disabled={isBusy}
                  data-karl-answer={wayOut.id}
                  onClick={() => answerAsk(wayOut)}
                  className={WAY_OUT_CLASS}
                >
                  {wayOut.label}
                </button>
              </div>
            )}
          </div>
        )}

        {/* The ladder and the waiting line are never both up: he is either
            choosing an answer or out on the drawing settling one. */}
        {/* A tool is running: say what it is waiting for, offer the one way to
            finish it and the one way out — and leave the question above,
            because he came here to work on the run, not to answer yet. */}
        {toolArm && (
          <div
            data-testid="karl-tool-strip"
            className="karl-await mt-3 rounded-lg border border-blue-300 bg-white p-2"
          >
            <div className="flex items-center gap-1.5 px-1 text-[13px] font-medium text-blue-700">
              <MousePointerClick size={14} className="flex-none text-blue-600" />
              {isPicking
                ? t("smartSurveyor.cablesBeta.askPickedCount", {
                    count: pickedCount,
                  })
                : t(
                    armedLine(view.arm as KarlAskArm).key,
                    armedLine(view.arm as KarlAskArm).params,
                  )}
            </div>
            <div className="mt-1.5 flex items-center gap-1.5">
              {isPicking && onConfirmPick && pickedCount > 0 && (
                <button
                  type="button"
                  onClick={onConfirmPick}
                  className="flex-1 rounded-md bg-blue-600 px-3 py-2 text-[13px] font-semibold text-white hover:bg-blue-700"
                >
                  {t(
                    view.arm?.kind === "boxRemove"
                      ? "smartSurveyor.cablesBeta.askPickedRemove"
                      : "smartSurveyor.cablesBeta.askPickedAdd",
                    { count: pickedCount },
                  )}
                </button>
              )}
              <button
                type="button"
                onClick={onCancelArm}
                className="rounded-md px-3 py-2.5 text-[14px] text-slate-600 hover:bg-slate-100 hover:text-slate-900"
              >
                {t("smartSurveyor.cablesBeta.askArmedCancel")}
              </button>
            </div>
          </div>
        )}

        {armed && (
          <div className="mt-3">
            <div className="karl-await flex items-center gap-1.5 rounded-md border border-blue-300 bg-white px-3 py-2.5 text-[13px] font-medium text-blue-700">
              <MousePointerClick size={14} className="flex-none text-blue-600" />
              {t(armed.key, armed.params)}
            </div>
            {view.arm?.kind === "drawZone" &&
              boardControls &&
              boardControls.zoneCorners >= 3 && (
                <button
                  type="button"
                  data-testid="karl-zone-finish"
                  onClick={boardControls.finishZone}
                  className="mt-1.5 flex w-full items-center justify-center gap-1.5 rounded-md bg-blue-600 px-3 py-2.5 text-[14px] font-semibold text-white hover:opacity-90"
                >
                  <Check size={15} className="flex-none" />
                  {t("smartSurveyor.cablesBeta.askZoneClose")}
                </button>
              )}
            <button
              type="button"
              onClick={onCancelArm}
              className={clsx(TONE_CLASS.quiet, "mt-1.5")}
            >
              {t("smartSurveyor.cablesBeta.askArmedCancel")}
            </button>
          </div>
        )}

        {/* Three real answers cannot be two icons. An ask that offers "show me
            the run" · "leave them out" · "put them back" keeps the worded
            ladder it always had — the pair is for a yes/no, and nothing else. */}
        {!armed && !pair && (
          <div
            data-testid="karl-answer-ladder"
            data-karl-answers="ladder"
            className={clsx(
              "mt-3 flex flex-col gap-2.5",
              isBusy && "pointer-events-none select-none opacity-50",
            )}
          >
            {ask.answers.map((answer, position) => (
              <div
                key={answer.id}
                // The way out sits below a line, never in the ladder of answers.
                className={clsx(
                  position === quietFrom &&
                    position > 0 &&
                    "mt-1 border-t border-slate-300 pt-2.5",
                )}
              >
                <button
                  type="button"
                  disabled={isBusy}
                  data-karl-answer={answer.id}
                  onClick={() => answerAsk(answer)}
                  className={TONE_CLASS[tones[position]]}
                >
                  {answer.label}
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Where the ladder used to stand: the things he can DO to the run, and
            the sheet it is drawn on as one more tile of the same shape in the
            schematic's own colour — a reference, not a verb, and the difference
            has to survive the move onto the row. The reference stays up while
            he is armed; a tool would be hidden. */}
        {/* Reshape arms nothing, so it shows no waiting line. Everything the
            mode offers that is NOT a mark on the drawing lives here, on the
            card that opened it: the two verbs that act on more than one thing
            at a time, the question about what the edit cut off, and the way
            out. */}
        {isReshapingThisRun && shapeControls && (
          <div
            data-testid="karl-reshape-hint"
            className="mt-2.5 rounded-lg border border-blue-300 bg-blue-50 px-2.5 py-2"
          >
            <div className="text-[13px] leading-relaxed text-blue-800">
              {t("smartSurveyor.cablesBeta.shapeEditPickUpHint")}
            </div>

            {/* He is never stopped from cutting a length: he cuts and then
                redraws it, so the devices his edit has orphaned are put to him
                ONCE, here, on the way out. The plan is already ringing them in
                red — the count is only what the sentence needs. */}
            {shapeControls.exitCount > 0 ? (
              <div data-testid="karl-reshape-exit" className="mt-2">
                <div className="text-[14px] font-semibold leading-snug text-slate-900">
                  {t("smartSurveyor.cablesBeta.shapeExitCut", {
                    count: shapeControls.exitCount,
                  })}
                </div>
                <div className="mt-2 flex items-stretch gap-2">
                  <button
                    type="button"
                    data-testid="karl-reshape-exit-confirm"
                    onClick={shapeControls.confirmExit}
                    className="flex min-h-[38px] flex-1 basis-0 items-center justify-center rounded-md bg-blue-600 px-3 py-2 text-center text-[14px] font-semibold text-white hover:opacity-90"
                  >
                    {t("smartSurveyor.cablesBeta.shapeExitRemove")}
                  </button>
                  <button
                    type="button"
                    data-testid="karl-reshape-exit-cancel"
                    onClick={shapeControls.cancelExit}
                    className="karl-hover-dwell flex min-h-[38px] flex-1 basis-0 items-center justify-center rounded-md border border-slate-300 bg-white px-3 py-2 text-center text-[14px] font-medium text-slate-800 hover:bg-slate-100"
                  >
                    {t("smartSurveyor.cablesBeta.shapeExitKeepEditing")}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <button
                  type="button"
                  data-testid="karl-reshape-done"
                  onClick={shapeControls.end}
                  className="karl-hover-dwell mt-2 flex min-h-[38px] w-full items-center justify-center gap-1.5 rounded-md border border-blue-600 bg-white px-3 py-2 text-[14px] font-semibold text-blue-700 hover:bg-blue-100"
                >
                  <Check size={15} className="flex-none" />
                  {t("smartSurveyor.cablesBeta.shapeEditDone")}
                </button>
              </>
            )}
          </div>
        )}

        {(tools || diagramHit) && (
          <div className="mt-2.5 border-t border-slate-200 pt-2.5">
            <div
              data-testid="karl-ask-tools"
              className="flex items-stretch gap-1"
            >
              {tools?.map((tool) => {
                const Icon = TOOL_ICON[tool.id] ?? Wrench;
                const isOn = isToolActive({ tool, activeTool });
                return (
                  <button
                    key={tool.id}
                    type="button"
                    disabled={isBusy}
                    aria-pressed={isOn}
                    data-karl-tool-kind="action"
                    data-karl-tool-active={isOn ? "true" : "false"}
                    title={t(`smartSurveyor.cablesBeta.${tool.labelKey}`)}
                    onClick={() => runTool(tool)}
                    className={clsx(
                      TOOL_CLASS,
                      isOn ? TOOL_ACTIVE_CLASS : TOOL_IDLE_CLASS,
                    )}
                  >
                    <Icon
                      size={17}
                      className={clsx(
                        "flex-none",
                        isOn ? "text-blue-700" : "text-slate-600",
                      )}
                    />
                    <span className="w-full truncate">
                      {t(`smartSurveyor.cablesBeta.${tool.labelKey}Short`)}
                    </span>
                  </button>
                );
              })}
              {diagramHit && (
                <button
                  type="button"
                  data-karl-diagram-ref={diagramHit.designator}
                  data-karl-tool-kind="reference"
                  aria-label={t("smartSurveyor.cablesBeta.askRouteOnPage", {
                    page: diagramHit.page,
                  })}
                  title={t("smartSurveyor.cablesBeta.askRouteOnPage", {
                    page: diagramHit.page,
                  })}
                  onClick={() => setIsDiagramPageOpen(true)}
                  className={clsx(TOOL_CLASS, REFERENCE_CLASS)}
                >
                  <FileText size={17} className="flex-none text-indigo-600" />
                  <span className="w-full truncate">
                    {t("smartSurveyor.cablesBeta.askRouteOnPageShort", {
                      page: diagramHit.page,
                    })}
                  </span>
                </button>
              )}
            </div>
          </div>
        )}

        {nameEntry && boardControls && (
          <BoardNameEntry
            key={nameEntry.kind === "rename" ? nameEntry.label : "add"}
            initialName={nameEntry.kind === "rename" ? (ask.subject ?? "") : ""}
            submitLabelKey={
              nameEntry.kind === "rename" ? "askBoardRenameSave" : "askBoardAddPlace"
            }
            onSubmit={(name) => {
              if (nameEntry.kind === "rename") boardControls.rename(nameEntry.label, name);
              else boardControls.add(name);
              setNameEntry(null);
            }}
            onCancel={() => setNameEntry(null)}
          />
        )}

        {feedsEntryFor && boardControls?.feeds?.byLabel[feedsEntryFor] && (
          <BoardFeedsEntry
            key={feedsEntryFor}
            feed={boardControls.feeds.byLabel[feedsEntryFor].feed}
            typesByCategory={boardControls.feeds.typesByCategory}
            onSave={(feed) => {
              boardControls.feeds?.save(feedsEntryFor, feed);
              setFeedsEntryFor(null);
            }}
            onCancel={() => setFeedsEntryFor(null)}
          />
        )}

        {heightEntry && (
          <HeightEntry
            initialM={heightEntry.heightM}
            onSubmit={(heightM) => {
              setHeightEntry(null);
              onAnswer(ask, {
                id: "right",
                label: t("smartSurveyor.cablesBeta.askHeightRight"),
                tone: "primary",
                action: { kind: "setHeight", target: heightEntry.target, heightM },
              });
            }}
            onCancel={() => setHeightEntry(null)}
          />
        )}

        {typeEntry && (
          // Picking or making a type IS the answer, as typing a height is.
          <div data-testid="karl-feeder-type-entry" className="mt-2.5">
            <PanelSelect
              value={typeEntry.cableType}
              options={typeEntry.options.map((type) => ({ value: type, label: type }))}
              placeholder={t("smartSurveyor.cablesBeta.cableTypePick")}
              ariaLabel={t("smartSurveyor.cablesBeta.askFeederTypeOther")}
              onPick={(cableType) => {
                setTypeEntry(null);
                onAnswer(ask, {
                  id: "right",
                  label: t("smartSurveyor.cablesBeta.askFeederTypeYes", { type: cableType }),
                  tone: "primary",
                  action: { kind: "setFeederType", toLabel: typeEntry.toLabel, cableType },
                });
              }}
              onCreate={(cableType) => {
                setTypeEntry(null);
                onAnswer(ask, {
                  id: "right",
                  label: t("smartSurveyor.cablesBeta.askFeederTypeYes", { type: cableType }),
                  tone: "primary",
                  action: { kind: "setFeederType", toLabel: typeEntry.toLabel, cableType: cableType.trim() },
                });
              }}
              createLabel={(text) => t("smartSurveyor.cablesBeta.cableTypesCreate", { name: text })}
            />
          </div>
        )}

        {boardControls?.addingName && (
          <div className="karl-await mt-2.5 flex items-center gap-1.5 rounded-md border border-blue-300 bg-white px-3 py-2.5 text-[13px] font-medium text-blue-700">
            <MousePointerClick size={14} className="flex-none text-blue-600" />
            {t("smartSurveyor.cablesBeta.askArmedPlace", {
              board: boardControls.addingName,
            })}
          </div>
        )}

        {/* Cancel is the only way out of an armed ask; a second one beside it
            reads as two different retreats. */}
        {/* Each way is drawn only while it leads somewhere, which is what the
            finished card above has always done. Forward is `rewind > 0`: it
            exists solely to bring him home after he stepped BACK, so on the
            walk itself it was a permanently grey control on the card he uses
            most — "is there any idea behind, it always kind of deactivated?".
            It cannot simply go: nothing but Forward returns `rewind` to zero
            without ANSWERING, so a man who stepped back to look would have to
            re-answer a question to get out. Back is dead in the same way on the
            very first ask, and is treated the same way. */}
        {!isArmed && (view.canGoBack || view.canGoForward) && (
          <div className="mt-3 flex items-center">
            {view.canGoBack && (
              <button
                type="button"
                onClick={onBack}
                disabled={isBusy}
                className={NAV_CLASS}
              >
                <ChevronLeft size={13} />
                {t("smartSurveyor.cablesBeta.askBack")}
              </button>
            )}
            {view.canGoForward && (
              <button
                type="button"
                onClick={onForward}
                disabled={isBusy}
                className={clsx(NAV_CLASS, "ml-auto")}
              >
                {t("smartSurveyor.cablesBeta.askForward")}
                <ChevronRight size={13} />
              </button>
            )}
          </div>
        )}
      </div>

      {/* The page opens over the walkthrough: leaving for the schematic is his
          choice to make, not the price of asking which page it is on. */}
      {diagramHit && (
        <DiagramPageModal
          open={isDiagramPageOpen}
          onClose={() => setIsDiagramPageOpen(false)}
          designator={diagramHit.designator}
          label={ask.subject}
          page={diagramHit.page}
          imageUrl={diagramHit.imageUrl}
          documentKey={diagramHit.documentKey}
          onOpenFullView={
            diagram
              ? () => {
                  setIsDiagramPageOpen(false);
                  diagram.open(diagramHit.designator);
                }
              : undefined
          }
        />
      )}
    </div>
  );
};
